import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabaseClient';
import { toast } from 'react-hot-toast';
import { showConfirm } from '../lib/swal';
import { formatearNumeroCL } from '../utils/formatUtils';
import { rendimientoSegunColor } from '../utils/rendimientoUtils';
import ExportarMaquinasExcel from '../components/ExportarMaquinasExcel';

export default function Maquinas() {
  const [maquinas, setMaquinas] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [modelosToner, setModelosToner] = useState([]);
  const [tiposToner, setTiposToner] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingMaquina, setEditingMaquina] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const [filtroModelo, setFiltroModelo] = useState('');
  const [filtroSerie, setFiltroSerie] = useState('');
  const [filtroToner, setFiltroToner] = useState('');
  const [filtroCliente, setFiltroCliente] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [mostrarFiltrosAvanzados, setMostrarFiltrosAvanzados] = useState(false);

  // Estados para el input de búsqueda de clientes
  const [clienteBusqueda, setClienteBusqueda] = useState('');
  const [clienteSeleccionado, setClienteSeleccionado] = useState('');

  const [form, setForm] = useState({
    modelo: '',
    serie: '',
    modelo_toner_id: '',
    tipo_toner_id: '',
    guia_entrega: '',
    departamento: '',
    fecha_instalacion: '',
    cliente_id: '',
    es_color: false,
    contador_inicial: '',
  });

  useEffect(() => {
    fetchMaquinas();
    fetchClientes();
    fetchModelosToner();
    fetchTiposToner();
  }, []);

  const fetchMaquinas = async () => {
    const { data, error } = await supabase
      .from('maquinas')
      .select('*, clientes(nombre), tipos_toner(nombre), toners:modelo_toner_id(modelo, rendimiento, rendimiento_color)')
      .order('fecha_instalacion', { ascending: false });
    if (!error) setMaquinas(data);
  };

  const fetchClientes = async () => {
    const { data } = await supabase.from('clientes').select('id, nombre');
    setClientes(data);
  };

  const fetchModelosToner = async () => {
    const { data } = await supabase.from('toners').select('id, modelo, rendimiento, rendimiento_color');
    setModelosToner(data);
  };

  const fetchTiposToner = async () => {
    const { data, error } = await supabase.from('tipos_toner').select('id, nombre');
    if (!error) setTiposToner(data);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const openModal = (maquina = null) => {
    if (maquina) {
      setForm({
        modelo: maquina.modelo,
        serie: maquina.serie,
        modelo_toner_id: maquina.modelo_toner_id || '',
        tipo_toner_id: maquina.tipo_toner_id || '',
        guia_entrega: maquina.guia_entrega || '',
        departamento: maquina.departamento || '',
        fecha_instalacion: maquina.fecha_instalacion || '',
        cliente_id: maquina.cliente_id || '',
        es_color: maquina.es_color || false,
        contador_inicial: maquina.contador_inicial || '',
      });
      setEditingMaquina(maquina.id);

      //Configurar busqueda de cliente para edicion
      if (maquina.cliente_id) {
        const cliente = clientes.find(c => c.id === maquina.cliente_id);
        if (cliente) {
          setClienteBusqueda(cliente.nombre);
          setClienteSeleccionado(cliente.id);
        }
      }
    } else {
      setForm({
        modelo: '',
        serie: '',
        modelo_toner_id: '',
        tipo_toner_id: '',
        guia_entrega: '',
        departamento: '',
        fecha_instalacion: '',
        cliente_id: '',
        es_color: false,
        contador_inicial: '',
      });
      setEditingMaquina(null);
      setClienteBusqueda('');
      setClienteSeleccionado('');
    }
    setModalOpen(true);
  };

  const saveMaquina = async () => {
    if (!form.modelo || !form.serie || !form.modelo_toner_id || !form.cliente_id || !form.tipo_toner_id) {
      return toast.error('Completa todos los campos obligatorios');
    }

    let response;
    if (editingMaquina) {
      response = await supabase.from('maquinas').update(form).eq('id', editingMaquina).select().single();
    } else {
      response = await supabase.from('maquinas').insert([form]).select().single();
    }

    if (response.error) {
      toast.error('Error al guardar máquina');
      return;
    }

    const maquina = response.data;

    // ---- Tóner de cada color (tabla toner_por_maquina) ----
    // Cada color hereda el rendimiento del tóner: el negro usa `rendimiento`
    // y Cyan/Magenta/Yellow usan `rendimiento_color` (si el tóner lo tiene).
    const tonerNuevoId = parseInt(form.modelo_toner_id);
    const colores = form.es_color ? ['Black', 'Cyan', 'Magenta', 'Yellow'] : ['Black'];
    const tonerElegido = modelosToner?.find(t => t.id === tonerNuevoId);
    const rendimientoDe = (color) => rendimientoSegunColor(tonerElegido, color) || null;
    const erroresColor = [];

    if (!editingMaquina) {
      // Máquina nueva: un registro por color
      const registros = colores.map((color) => ({
        maquina_id: maquina.id,
        color,
        modelo_toner_id: tonerNuevoId,
        rendimiento_estimado: rendimientoDe(color),
      }));
      const { error } = await supabase.from('toner_por_maquina').insert(registros);
      if (error) erroresColor.push(error);
    } else {
      // Máquina editada: mantener al día el tóner de cada color.
      const anterior = maquinas.find(m => m.id === editingMaquina);
      const tonerAnteriorId = anterior?.modelo_toner_id;

      const { data: actuales } = await supabase
        .from('toner_por_maquina')
        .select('id, color, modelo_toner_id')
        .eq('maquina_id', editingMaquina);

      // a) Si se cambió el tóner de la máquina, los colores que usaban el
      //    tóner anterior pasan al nuevo (si algún color tenía otro, se respeta).
      if (tonerAnteriorId && tonerAnteriorId !== tonerNuevoId) {
        const filasACambiar = (actuales || []).filter(f => f.modelo_toner_id === tonerAnteriorId);
        for (const fila of filasACambiar) {
          const { error } = await supabase
            .from('toner_por_maquina')
            .update({ modelo_toner_id: tonerNuevoId, rendimiento_estimado: rendimientoDe(fila.color) })
            .eq('id', fila.id);
          if (error) erroresColor.push(error);
        }
      }

      // b) Si ahora es de color y le faltan colores, se agregan.
      const coloresExistentes = new Set((actuales || []).map(f => f.color));
      const faltantes = colores.filter(c => !coloresExistentes.has(c));
      if (faltantes.length) {
        const { error } = await supabase.from('toner_por_maquina').insert(
          faltantes.map((color) => ({
            maquina_id: editingMaquina,
            color,
            modelo_toner_id: tonerNuevoId,
            rendimiento_estimado: rendimientoDe(color),
          }))
        );
        if (error) erroresColor.push(error);
      }
    }

    if (erroresColor.length) {
      console.error('Error al actualizar el tóner por color:', erroresColor);
      toast.error('La máquina se guardó, pero no se pudo actualizar el tóner de cada color');
    }

    fetchMaquinas();
    setModalOpen(false);
    setEditingMaquina(null);
    toast.success('Máquina guardada correctamente');
  };

  const deleteMaquina = async (id) => {
    const confirmado = await showConfirm(
      '¿Eliminar esta máquina?',
      'Esta acción no se puede deshacer.',
      { peligro: true }
    );
    if (confirmado) {
      await supabase.from('maquinas').delete().eq('id', id);
      fetchMaquinas();
    }
  };

  const terminoBusqueda = busqueda.trim().toLowerCase();
  const maquinasFiltradas = maquinas.filter((m) => {
    // Búsqueda global: revisa modelo, serie, tipo de tóner y cliente a la vez
    const coincideBusqueda = terminoBusqueda
      ? (
          (m.modelo || '').toLowerCase().includes(terminoBusqueda) ||
          (m.serie || '').toLowerCase().includes(terminoBusqueda) ||
          (m.tipos_toner?.nombre || '').toLowerCase().includes(terminoBusqueda) ||
          (m.clientes?.nombre || '').toLowerCase().includes(terminoBusqueda)
        )
      : true;

    // Filtros avanzados (opcionales)
    const coincideFiltros =
      m.modelo.toLowerCase().includes(filtroModelo.toLowerCase()) &&
      m.serie.toLowerCase().includes(filtroSerie.toLowerCase()) &&
      (m.tipos_toner?.nombre || '').toLowerCase().includes(filtroToner.toLowerCase()) &&
      (m.clientes?.nombre || '').toLowerCase().includes(filtroCliente.toLowerCase());

    return coincideBusqueda && coincideFiltros;
  });

  const hayFiltrosAvanzadosActivos = filtroModelo || filtroSerie || filtroToner || filtroCliente;

  const limpiarFiltros = () => {
    setBusqueda('');
    setFiltroModelo('');
    setFiltroSerie('');
    setFiltroToner('');
    setFiltroCliente('');
    setCurrentPage(1);
  };

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = maquinasFiltradas.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(maquinasFiltradas.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handlePrevious = () => {
    setCurrentPage(prev => Math.max(prev - 1, 1));
  };
  
  const handleNext = () => {
    setCurrentPage(prev => Math.min(prev + 1, totalPages));
  };

  return (
    <div className="ml-0 lg:ml-72 p-4 sm:p-6 lg:p-8 pt-16 lg:pt-6 min-h-screen animate-fadeIn">
      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-2">
              <span className="text-gradient">Gestión de Máquinas</span>
            </h1>
            <p className="text-gray-600 flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
              </svg>
              {maquinas.length} {maquinas.length === 1 ? 'máquina registrada' : 'máquinas registradas'}
            </p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <ExportarMaquinasExcel maquinas={maquinas} />
            <button
              onClick={() => openModal()}
              className="inline-flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-sky-500 to-blue-600 text-white font-medium rounded-xl hover:shadow-lg transition-all duration-200 transform hover:scale-105 active:scale-95"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Nueva Máquina
            </button>
          </div>
        </div>
      </div>

      {/* Buscador principal */}
      <div className="mb-6">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
            <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <input
            type="text"
            value={busqueda}
            onChange={(e) => { setBusqueda(e.target.value); setCurrentPage(1); }}
            placeholder="Buscar por modelo, serie, tipo de tóner o cliente..."
            className="w-full pl-12 pr-10 py-3.5 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 bg-white text-base"
          />
          {busqueda && (
            <button
              onClick={() => { setBusqueda(''); setCurrentPage(1); }}
              className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
              title="Limpiar búsqueda"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
        <div className="flex items-center justify-between mt-2 px-1">
          <span className="text-sm text-gray-500">
            {maquinasFiltradas.length} {maquinasFiltradas.length === 1 ? 'máquina' : 'máquinas'}
            {(terminoBusqueda || filtroModelo || filtroSerie || filtroToner || filtroCliente) ? ' encontradas' : ' en total'}
          </span>
          <button
            onClick={() => setMostrarFiltrosAvanzados(v => !v)}
            className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-800 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
            {mostrarFiltrosAvanzados ? 'Ocultar filtros avanzados' : 'Filtros avanzados'}
          </button>
        </div>
      </div>

      {/* Filtros avanzados (colapsables) */}
      {mostrarFiltrosAvanzados && (
      <div className="bg-white shadow-xl rounded-2xl p-4 sm:p-6 mb-6 border border-gray-100">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Modelo</label>
            <select
              value={filtroModelo}
              onChange={(e) => setFiltroModelo(e.target.value)}
              className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 bg-white"
            >
              <option value="">Todos los modelos</option>
              {[...new Set(maquinas.map((m) => m.modelo))].map((modelo) => (
                <option key={modelo} value={modelo}>
                  {modelo}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Serie</label>
            <input
              type="text"
              value={filtroSerie}
              onChange={(e) => setFiltroSerie(e.target.value)}
              className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 bg-white"
              placeholder="Buscar por serie..."
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Tipo de Tóner</label>
            <select
              value={filtroToner}
              onChange={(e) => setFiltroToner(e.target.value)}
              className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 bg-white"
            >
              <option value="">Todos los tipos</option>
              {tiposToner.map((tipo) => (
                <option key={tipo.id} value={tipo.nombre}>
                  {tipo.nombre}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Cliente</label>
            <input
              type="text"
              value={filtroCliente}
              onChange={(e) => setFiltroCliente(e.target.value)}
              className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 bg-white"
              placeholder="Buscar por cliente..."
            />
          </div>
        </div>
        {(filtroModelo || filtroSerie || filtroToner || filtroCliente) && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <button
              onClick={() => {
                setFiltroModelo('');
                setFiltroSerie('');
                setFiltroToner('');
                setFiltroCliente('');
              }}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-all duration-200"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              Limpiar filtros
            </button>
          </div>
        )}
      </div>
      )}
      {/* Tabla */}
      <div className="bg-white shadow-xl rounded-2xl overflow-hidden border border-gray-100 hidden md:block">
        {currentItems.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-gray-50 to-gray-100 border-b-2 border-gray-200">
                <tr>
                  <th className="px-4 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">N°</th>
                  <th className="px-4 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Modelo</th>
                  <th className="px-4 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Serie</th>
                  <th className="px-4 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Tipo de Tóner</th>
                  <th className="px-4 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Contador Inicial</th>
                  <th className="px-4 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Guía</th>
                  <th className="px-4 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Depto</th>
                  <th className="px-4 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Instalación</th>
                  <th className="px-4 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Cliente</th>
                  <th className="px-4 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Color</th>
                  <th className="px-4 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {currentItems.map((m, index) => (
                  <tr
                    key={m.id}
                    className="hover:bg-gradient-to-r hover:from-sky-50 hover:to-blue-50 transition-all duration-200"
                  >
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {indexOfFirstItem + index + 1}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="text-sm font-semibold text-gray-900">{m.modelo}</div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 font-mono">{m.serie}</div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className="inline-flex px-3 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                        {m.tipos_toner?.nombre}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      {m.contador_inicial ? (
                        <span className="text-sm font-medium text-gray-900">
                          {new Intl.NumberFormat('es-CL').format(m.contador_inicial)}
                        </span>
                      ) : (
                        <span className="text-sm text-gray-400 italic">No definido</span>
                      )}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">
                      {m.guia_entrega || <span className="text-gray-400">-</span>}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">
                      {m.departamento || <span className="text-gray-400">-</span>}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">
                      {m.fecha_instalacion || <span className="text-gray-400">-</span>}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{m.clientes?.nombre}</div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      {m.es_color ? (
                        <span className="inline-flex px-3 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-800">
                          Color
                        </span>
                      ) : (
                        <span className="inline-flex px-3 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
                          BK
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => openModal(m)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-500 hover:bg-amber-600 text-white rounded-lg transition-all duration-200 hover:shadow-lg transform hover:scale-105"
                          title="Editar máquina"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => deleteMaquina(m.id)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-all duration-200 hover:shadow-lg transform hover:scale-105"
                          title="Eliminar máquina"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-16 px-4">
            <div className="p-4 bg-gradient-to-br from-sky-100 to-blue-100 rounded-full mb-4">
              <svg className="w-12 h-12 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
              </svg>
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-2">No hay máquinas registradas</h3>
            <p className="text-gray-600 text-center max-w-md mb-6">
              Comienza agregando la primera máquina del sistema
            </p>
            <button
              onClick={() => openModal()}
              className="inline-flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-sky-500 to-blue-600 text-white font-medium rounded-xl hover:shadow-lg transition-all duration-200 transform hover:scale-105"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Agregar Primera Máquina
            </button>
          </div>
        )}
      </div>

      {/* Vista de tarjetas — solo en móvil */}
      <div className="md:hidden space-y-3">
        {currentItems.length === 0 ? (
          <div className="bg-white shadow-lg rounded-2xl border border-gray-100 flex flex-col items-center justify-center py-12 px-4">
            <div className="p-4 bg-gradient-to-br from-sky-100 to-blue-100 rounded-full mb-4">
              <svg className="w-10 h-10 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
              </svg>
            </div>
            <h3 className="text-base font-bold text-gray-900 mb-1">No hay máquinas registradas</h3>
            <p className="text-sm text-gray-600 text-center">Comienza agregando la primera máquina</p>
          </div>
        ) : (
          currentItems.map((m, index) => (
            <div key={m.id} className="bg-white shadow-lg rounded-2xl border border-gray-100 p-4">
              {/* Encabezado: número + modelo + color */}
              <div className="flex items-start gap-3 mb-3">
                <span className="flex-shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-br from-sky-100 to-blue-100 text-blue-700 font-semibold text-xs">
                  {indexOfFirstItem + index + 1}
                </span>
                <div className="flex-1 min-w-0">
                  <h3 className="font-bold text-gray-900 text-sm leading-snug break-words">
                    {m.modelo || 'Sin modelo'}
                  </h3>
                  <p className="font-mono text-xs text-gray-500 mt-0.5 break-all">{m.serie || '—'}</p>
                </div>
                {m.es_color && (
                  <span className="flex-shrink-0 inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-pink-100 text-pink-800">
                    Color
                  </span>
                )}
              </div>

              {/* Datos */}
              <dl className="space-y-2 text-sm">
                <div className="flex justify-between gap-3">
                  <dt className="text-gray-500 flex-shrink-0">Cliente</dt>
                  <dd className="text-gray-800 text-right break-words">{m.clientes?.nombre || '—'}</dd>
                </div>
                <div className="flex justify-between items-center gap-3">
                  <dt className="text-gray-500 flex-shrink-0">Tipo de tóner</dt>
                  <dd className="text-right">
                    {m.tipos_toner?.nombre ? (
                      <span className="inline-flex px-2 py-0.5 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                        {m.tipos_toner.nombre}
                      </span>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </dd>
                </div>
                {m.departamento && (
                  <div className="flex justify-between gap-3">
                    <dt className="text-gray-500 flex-shrink-0">Departamento</dt>
                    <dd className="text-gray-800 text-right break-words">{m.departamento}</dd>
                  </div>
                )}
                {m.guia_entrega && (
                  <div className="flex justify-between gap-3">
                    <dt className="text-gray-500 flex-shrink-0">Guía</dt>
                    <dd className="text-gray-800 text-right">{m.guia_entrega}</dd>
                  </div>
                )}
                {m.contador_inicial !== null && m.contador_inicial !== undefined && (
                  <div className="flex justify-between gap-3">
                    <dt className="text-gray-500 flex-shrink-0">Contador inicial</dt>
                    <dd className="text-gray-800 text-right">{formatearNumeroCL(m.contador_inicial)}</dd>
                  </div>
                )}
                {m.fecha_instalacion && (
                  <div className="flex justify-between gap-3">
                    <dt className="text-gray-500 flex-shrink-0">Instalación</dt>
                    <dd className="text-gray-800 text-right">
                      {new Date(m.fecha_instalacion).toLocaleDateString('es-CL')}
                    </dd>
                  </div>
                )}
              </dl>

              {/* Acciones */}
              <div className="flex items-center gap-2 mt-4 pt-3 border-t border-gray-100">
                <button
                  onClick={() => openModal(m)}
                  className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-lg hover:bg-amber-100 transition-all duration-200"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  Editar
                </button>
                <button
                  onClick={() => deleteMaquina(m.id)}
                  className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-all duration-200"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  Eliminar
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Paginación */}
      {totalPages > 1 && (
        <div className="mt-6 flex flex-wrap justify-center items-center gap-2 px-4">
          <button
            onClick={handlePrevious}
            disabled={currentPage === 1}
            className="inline-flex items-center gap-2 px-4 py-2 bg-white border-2 border-gray-200 text-gray-700 font-medium rounded-lg hover:border-blue-500 hover:text-blue-600 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:border-gray-200 disabled:hover:text-gray-700 transition-all duration-200"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Anterior
          </button>

          {/* Mostrar solo páginas relevantes para evitar overflow */}
          {(() => {
            const maxVisiblePages = 5;
            let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
            let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

            if (endPage - startPage + 1 < maxVisiblePages) {
              startPage = Math.max(1, endPage - maxVisiblePages + 1);
            }

            const pages = [];

            // Primera página si no está visible
            if (startPage > 1) {
              pages.push(
                <button
                  key={1}
                  onClick={() => handlePageChange(1)}
                  className="w-10 h-10 flex items-center justify-center bg-white border-2 border-gray-200 text-gray-700 font-medium rounded-lg hover:border-blue-500 hover:text-blue-600 transition-all duration-200"
                >
                  1
                </button>
              );
              if (startPage > 2) {
                pages.push(
                  <span key="ellipsis1" className="px-2 text-gray-400">
                    ...
                  </span>
                );
              }
            }

            // Páginas visibles
            for (let i = startPage; i <= endPage; i++) {
              pages.push(
                <button
                  key={i}
                  onClick={() => handlePageChange(i)}
                  className={`w-10 h-10 flex items-center justify-center font-medium rounded-lg transition-all duration-200 ${
                    currentPage === i
                      ? 'bg-gradient-to-r from-sky-500 to-blue-600 text-white shadow-lg transform scale-110'
                      : 'bg-white border-2 border-gray-200 text-gray-700 hover:border-blue-500 hover:text-blue-600'
                  }`}
                >
                  {i}
                </button>
              );
            }

            // Última página si no está visible
            if (endPage < totalPages) {
              if (endPage < totalPages - 1) {
                pages.push(
                  <span key="ellipsis2" className="px-2 text-gray-400">
                    ...
                  </span>
                );
              }
              pages.push(
                <button
                  key={totalPages}
                  onClick={() => handlePageChange(totalPages)}
                  className="w-10 h-10 flex items-center justify-center bg-white border-2 border-gray-200 text-gray-700 font-medium rounded-lg hover:border-blue-500 hover:text-blue-600 transition-all duration-200"
                >
                  {totalPages}
                </button>
              );
            }

            return pages;
          })()}

          <button
            onClick={handleNext}
            disabled={currentPage === totalPages}
            className="inline-flex items-center gap-2 px-4 py-2 bg-white border-2 border-gray-200 text-gray-700 font-medium rounded-lg hover:border-blue-500 hover:text-blue-600 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:border-gray-200 disabled:hover:text-gray-700 transition-all duration-200"
          >
            Siguiente
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      )}

      {/* Modal */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            {/* Header del Modal */}
            <div className="bg-gradient-to-r from-sky-500 to-blue-600 px-6 py-5 rounded-t-2xl">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white bg-opacity-20 rounded-lg">
                  <svg className="w-6 h-6 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-white">
                  {editingMaquina ? 'Editar Máquina' : 'Nueva Máquina'}
                </h3>
              </div>
            </div>

            {/* Body del Modal */}
            <div className="p-6 space-y-5">
              {/* Información Básica */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Modelo <span className="text-red-500">*</span>
                  </label>
                  <input
                    name="modelo"
                    placeholder="Ej: imageRUNNER ADVANCE C5535i"
                    value={form.modelo}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Serie <span className="text-red-500">*</span>
                  </label>
                  <input
                    name="serie"
                    placeholder="Ej: ABC123456"
                    value={form.serie}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 font-mono"
                  />
                </div>
              </div>

              {/* Información de Tóner */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Modelo de Tóner <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="modelo_toner_id"
                    value={form.modelo_toner_id}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200"
                  >
                    <option value="">-- Seleccionar modelo --</option>
                    {modelosToner.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.modelo}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Tipo de Tóner <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="tipo_toner_id"
                    value={form.tipo_toner_id}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200"
                  >
                    <option value="">-- Seleccionar tipo --</option>
                    {tiposToner.map((tipo) => (
                      <option key={tipo.id} value={tipo.id}>
                        {tipo.nombre}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Contador Inicial */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Contador Inicial
                </label>
                <input
                  name="contador_inicial"
                  type="number"
                  placeholder="Ej: 15000"
                  value={form.contador_inicial}
                  onChange={handleChange}
                  className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200"
                  min="0"
                />
                <div className="mt-2 flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <svg className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="text-sm text-blue-800">
                    Este será el contador de referencia para el primer cambio de tóner
                  </span>
                </div>
              </div>

              {/* Información de Instalación */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Guía de Entrega
                  </label>
                  <input
                    name="guia_entrega"
                    placeholder="Ej: GE-2024-001"
                    value={form.guia_entrega}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Departamento
                  </label>
                  <input
                    name="departamento"
                    placeholder="Ej: Administración"
                    value={form.departamento}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Fecha de Instalación
                  </label>
                  <input
                    type="date"
                    name="fecha_instalacion"
                    value={form.fecha_instalacion}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200"
                  />
                </div>
              </div>

              {/* Cliente - Autocomplete */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Cliente <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={clienteBusqueda}
                    onChange={(e) => {
                      setClienteBusqueda(e.target.value);
                      setClienteSeleccionado('');
                      setForm((prev) => ({ ...prev, cliente_id: '' }));
                    }}
                    className="w-full px-4 py-2.5 pr-10 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200"
                    placeholder="Escriba para buscar cliente..."
                  />
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>

                  {clienteBusqueda && !clienteSeleccionado && (
                    <div className="absolute z-20 w-full mt-1 bg-white border-2 border-gray-200 rounded-xl shadow-xl max-h-60 overflow-y-auto">
                      {clientes
                        .filter((cliente) =>
                          cliente.nombre.toLowerCase().includes(clienteBusqueda.toLowerCase())
                        )
                        .slice(0, 10)
                        .map((cliente) => (
                          <div
                            key={cliente.id}
                            onClick={() => {
                              setClienteSeleccionado(cliente.id);
                              setClienteBusqueda(cliente.nombre);
                              setForm((prev) => ({ ...prev, cliente_id: cliente.id }));
                            }}
                            className="px-4 py-3 hover:bg-gradient-to-r hover:from-sky-50 hover:to-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0 transition-all duration-200"
                          >
                            <div className="font-medium text-gray-900">{cliente.nombre}</div>
                          </div>
                        ))}
                      {clientes.filter((cliente) =>
                        cliente.nombre.toLowerCase().includes(clienteBusqueda.toLowerCase())
                      ).length === 0 && (
                        <div className="px-4 py-6 text-gray-500 text-center">
                          <svg className="w-12 h-12 mx-auto mb-2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <div className="text-sm font-medium">No se encontraron clientes</div>
                        </div>
                      )}
                    </div>
                  )}

                  {clienteSeleccionado && (
                    <div className="mt-2 p-3 bg-green-50 border-2 border-green-200 rounded-lg flex items-center gap-2">
                      <svg className="w-5 h-5 text-green-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="text-sm text-green-800 font-medium">
                        Cliente seleccionado: {clienteBusqueda}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Tipo de Máquina */}
              <div className="p-4 bg-gradient-to-r from-sky-50 to-blue-50 rounded-xl border-2 border-blue-200">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="es_color"
                    checked={form.es_color}
                    onChange={handleChange}
                    className="w-5 h-5 text-blue-600 border-2 border-gray-300 rounded focus:ring-2 focus:ring-blue-500 cursor-pointer"
                  />
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                    </svg>
                    <span className="text-sm font-semibold text-gray-700">
                      ¿Es máquina color?
                    </span>
                  </div>
                </label>
              </div>
            </div>

            {/* Footer del Modal */}
            <div className="px-6 py-4 bg-gray-50 rounded-b-2xl border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setModalOpen(false)}
                className="px-6 py-2.5 bg-white border-2 border-gray-300 text-gray-700 font-medium rounded-xl hover:bg-gray-50 hover:border-gray-400 transition-all duration-200"
              >
                Cancelar
              </button>
              <button
                onClick={saveMaquina}
                className="px-6 py-2.5 bg-gradient-to-r from-sky-500 to-blue-600 text-white font-medium rounded-xl hover:shadow-lg transition-all duration-200 transform hover:scale-105"
              >
                {editingMaquina ? 'Guardar Cambios' : 'Crear Máquina'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );  
}
